import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

/**
 * Sticky top navigation for the marketing landing page.
 *
 * Palette adheres to existing tokens:
 *  - Base:      bg-primary   (deep slate)
 *  - Ink:       text-primary-foreground (warm white)
 *  - CTA:       bg-accent    (amber)
 *
 * Scroll >8px flips to a slightly stronger blur + hairline border so the nav
 * doesn't sit ambiguously on top of the hero backdrop.
 *
 * Mobile: hamburger reveals the same links in a stacked drawer. No overlay
 * scrim — the drawer inherits the primary bg so it reads as part of the nav
 * (Linear/Vercel-style, not modal-style).
 */
export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const links = [
    { href: "#features", label: "Features" },
    { href: "#product",  label: "Product" },
    { href: "#pricing",  label: "Pricing" },
    { href: "#faq",      label: "FAQ" },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-200 ${
        scrolled
          ? "backdrop-blur-xl bg-primary/90 border-b border-primary-foreground/10"
          : "bg-primary/60 backdrop-blur"
      }`}
      data-testid="landing-nav"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="landing-nav-logo">
          <div className="h-8 w-8 rounded-md bg-accent grid place-items-center text-accent-foreground font-black text-lg group-hover:rotate-3 transition-transform">
            J
          </div>
          <span className="font-black tracking-tight text-lg text-primary-foreground">
            JobsBook
            <span className="text-accent">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-semibold text-primary-foreground/70 hover:text-primary-foreground transition-colors"
              data-testid={`landing-nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/login"
            className="text-sm font-semibold text-primary-foreground/70 hover:text-primary-foreground transition-colors"
            data-testid="landing-nav-signin"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="text-sm font-bold bg-accent text-accent-foreground px-4 py-2 rounded-md hover:bg-accent/90 transition-all hover:-translate-y-[1px]"
            data-testid="landing-nav-signup"
          >
            Start free
          </Link>
        </div>

        <button
          type="button"
          className="md:hidden text-primary-foreground p-2 -mr-2"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="landing-nav-mobile-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-primary-foreground/10 bg-primary/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-2.5 text-primary-foreground/80 font-semibold"
                data-testid={`landing-nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <div className="mt-2 pt-3 border-t border-primary-foreground/10 flex gap-3">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="flex-1 text-center py-2.5 border border-primary-foreground/20 rounded-md text-sm font-semibold text-primary-foreground"
                data-testid="landing-nav-mobile-signin"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                onClick={() => setOpen(false)}
                className="flex-1 text-center py-2.5 bg-accent text-accent-foreground rounded-md text-sm font-bold"
                data-testid="landing-nav-mobile-signup"
              >
                Start free
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
